// Quote of the day — 100 entries in English, French and Arabic.
// One is shown per day, chosen deterministically from the date.
//
// `d: true` marks an attribution that is popularly used but not verifiable in
// the named person's own writing. Those render as "attributed to X" /
// "attribué à X" / "منسوب إلى X" rather than a flat claim.
//
// `aa` is the author's name in Arabic script, so the RTL page does not break
// into Latin characters mid-sentence.

export const QUOTES = [
  // — Omar's own picks —
  { en: "You miss 100% of the shots you don't take.",
    fr: "On rate 100 % des occasions qu'on ne tente pas.",
    ar: "تُضيّع 100% من الفرص التي لا تحاولها.",
    a: "Wayne Gretzky", aa: "واين غريتزكي" },
  { en: "Whether you think you can or you think you can't, you're right.",
    fr: "Que vous pensiez en être capable ou non, vous avez raison.",
    ar: "سواء ظننتَ أنك تستطيع أو لا تستطيع، فأنت مُحِقّ.",
    a: "Henry Ford", aa: "هنري فورد", d: true },
  { en: "You are never too old to set another goal or to dream a new dream.",
    fr: "On n'est jamais trop vieux pour se fixer un nouveau but ou faire un nouveau rêve.",
    ar: "لن تكبر يومًا عن أن تضع هدفًا جديدًا أو تحلم حلمًا جديدًا.",
    a: "C.S. Lewis", aa: "سي. إس. لويس", d: true },
  { en: "Accept what is, let go of what was, and have faith in what will be.",
    fr: "Acceptez ce qui est, lâchez ce qui fut, et ayez foi en ce qui sera.",
    ar: "اقبل ما هو كائن، وأفلِت ما كان، وثِق بما سيكون.",
    a: "Sonia Ricotti", aa: "سونيا ريكوتي" },
  { en: "Whatever you are, be a good one.",
    fr: "Quoi que vous soyez, soyez-le bien.",
    ar: "أيًّا كان ما أنت عليه، فكن فيه بارعًا.",
    a: "Abraham Lincoln", aa: "أبراهام لينكولن", d: true },
  { en: "The future belongs to the competent. Get good, get better, be the best.",
    fr: "L'avenir appartient aux compétents. Devenez bon, devenez meilleur, soyez le meilleur.",
    ar: "المستقبل لأهل الكفاءة. كن جيدًا، ثم أفضل، ثم كن الأفضل.",
    a: "Brian Tracy", aa: "برايان تريسي" },
  { en: "Good is the enemy of great.",
    fr: "Le bien est l'ennemi de l'excellent.",
    ar: "الجيّد عدوّ العظيم.",
    a: "Jim Collins", aa: "جيم كولينز" },
  { en: "Believe you can and you're halfway there.",
    fr: "Croyez que vous en êtes capable et vous avez déjà fait la moitié du chemin.",
    ar: "آمِن أنك تستطيع، فتكون قد قطعتَ نصف الطريق.",
    a: "Theodore Roosevelt", aa: "ثيودور روزفلت" },

  // — Stoics —
  { en: "We suffer more often in imagination than in reality.",
    fr: "Nous souffrons plus souvent en imagination qu'en réalité.",
    ar: "نتألم في خيالنا أكثر مما نتألم في الواقع.",
    a: "Seneca", aa: "سينيكا" },
  { en: "It is not that we have a short time to live, but that we waste a lot of it.",
    fr: "Ce n'est pas que nous ayons peu de temps à vivre : c'est que nous en perdons beaucoup.",
    ar: "ليست المشكلة أن العمر قصير، بل أننا نُهدر كثيرًا منه.",
    a: "Seneca", aa: "سينيكا" },
  { en: "Difficulties strengthen the mind, as labour does the body.",
    fr: "Les difficultés fortifient l'esprit comme l'effort fortifie le corps.",
    ar: "الشدائد تُقوّي العقل كما يُقوّي الجهدُ الجسد.",
    a: "Seneca", aa: "سينيكا" },
  { en: "Begin at once to live, and count each separate day as a separate life.",
    fr: "Commencez à vivre dès maintenant, et comptez chaque jour comme une vie entière.",
    ar: "ابدأ الحياة الآن، واعتبر كل يوم حياةً قائمة بذاتها.",
    a: "Seneca", aa: "سينيكا" },
  { en: "You have power over your mind — not outside events. Realise this, and you will find strength.",
    fr: "Vous avez du pouvoir sur votre esprit, non sur les événements. Comprenez-le, et vous trouverez la force.",
    ar: "لك سلطان على عقلك لا على الأحداث. أدرِك هذا تجد القوة.",
    a: "Marcus Aurelius", aa: "ماركوس أوريليوس" },
  { en: "Waste no more time arguing what a good man should be. Be one.",
    fr: "Ne perdez plus de temps à discuter de ce que doit être un homme de bien. Soyez-le.",
    ar: "لا تُضِع الوقت في الجدل حول صفات الرجل الصالح. كُن واحدًا منهم.",
    a: "Marcus Aurelius", aa: "ماركوس أوريليوس" },
  { en: "The impediment to action advances action. What stands in the way becomes the way.",
    fr: "L'obstacle à l'action fait avancer l'action. Ce qui barre la route devient la route.",
    ar: "ما يعيق الفعل يدفعه. وما يقف في الطريق يصير هو الطريق.",
    a: "Marcus Aurelius", aa: "ماركوس أوريليوس" },
  { en: "Confine yourself to the present.",
    fr: "Tenez-vous-en au présent.",
    ar: "اِحصُر نفسك في اللحظة الحاضرة.",
    a: "Marcus Aurelius", aa: "ماركوس أوريليوس" },
  { en: "First say to yourself what you would be; then do what you have to do.",
    fr: "Dites-vous d'abord ce que vous voulez être ; faites ensuite ce qu'il faut faire.",
    ar: "قرّر أولًا ما تريد أن تكون، ثم افعل ما يلزم لذلك.",
    a: "Epictetus", aa: "إبكتيتوس" },
  { en: "No man is free who is not master of himself.",
    fr: "Nul n'est libre s'il n'est maître de lui-même.",
    ar: "لا يكون حرًّا من لا يملك زمام نفسه.",
    a: "Epictetus", aa: "إبكتيتوس" },
  { en: "It's not what happens to you, but how you react to it that matters.",
    fr: "Ce n'est pas ce qui vous arrive qui compte, mais votre façon d'y réagir.",
    ar: "ليس المهم ما يحدث لك، بل كيف تستجيب له.",
    a: "Epictetus", aa: "إبكتيتوس" },
  { en: "Don't explain your philosophy. Embody it.",
    fr: "N'expliquez pas votre philosophie. Incarnez-la.",
    ar: "لا تشرح فلسفتك، بل جسّدها.",
    a: "Epictetus", aa: "إبكتيتوس" },

  // — Classical & Eastern —
  { en: "A journey of a thousand miles begins with a single step.",
    fr: "Un voyage de mille lieues commence par un seul pas.",
    ar: "رحلة الألف ميل تبدأ بخطوة واحدة.",
    a: "Lao Tzu", aa: "لاو تزو" },
  { en: "He who conquers himself is the mightiest warrior.",
    fr: "Celui qui se vainc lui-même est le plus puissant des guerriers.",
    ar: "من غلب نفسه فهو أعظم المحاربين.",
    a: "Confucius", aa: "كونفوشيوس" },
  { en: "The man who moves a mountain begins by carrying away small stones.",
    fr: "Celui qui déplace une montagne commence par emporter de petites pierres.",
    ar: "من يزيح جبلًا يبدأ بحمل الحصى الصغير.",
    a: "Confucius", aa: "كونفوشيوس" },
  { en: "It does not matter how slowly you go, so long as you do not stop.",
    fr: "Peu importe la lenteur, tant que vous ne vous arrêtez pas.",
    ar: "لا يهم بطء سيرك ما دمت لا تتوقف.",
    a: "Confucius", aa: "كونفوشيوس", d: true },
  { en: "Well begun is half done.",
    fr: "Bien commencer, c'est déjà à moitié fait.",
    ar: "البداية الحسنة نصف العمل.",
    a: "Aristotle", aa: "أرسطو" },
  { en: "Knowing yourself is the beginning of all wisdom.",
    fr: "Se connaître soi-même est le début de toute sagesse.",
    ar: "معرفة النفس بداية كل حكمة.",
    a: "Aristotle", aa: "أرسطو", d: true },
  { en: "We are what we repeatedly do. Excellence, then, is not an act but a habit.",
    fr: "Nous sommes ce que nous faisons de façon répétée. L'excellence n'est donc pas un acte, mais une habitude.",
    ar: "نحن ما نُكرّره من أفعال. فالتميّز إذًا ليس فعلًا، بل عادة.",
    a: "Will Durant", aa: "ويل ديورانت" },
  { en: "Fall seven times, stand up eight.",
    fr: "Tombe sept fois, relève-toi huit.",
    ar: "اسقط سبعًا وانهض ثمانيًا.",
    a: "Japanese proverb", aa: "مثل ياباني" },
  { en: "The best time to plant a tree was twenty years ago. The second best time is now.",
    fr: "Le meilleur moment pour planter un arbre était il y a vingt ans. Le deuxième meilleur moment, c'est maintenant.",
    ar: "خير وقت لغرس شجرة كان قبل عشرين عامًا، وثاني خير وقت هو الآن.",
    a: "Chinese proverb", aa: "مثل صيني" },
  { en: "Yesterday I was clever, so I wanted to change the world. Today I am wise, so I am changing myself.",
    fr: "Hier j'étais intelligent, je voulais changer le monde. Aujourd'hui je suis sage, je me change moi-même.",
    ar: "بالأمس كنتُ ذكيًّا فأردتُ تغيير العالم، واليوم صرتُ حكيمًا فأنا أُغيّر نفسي.",
    a: "Rumi", aa: "جلال الدين الرومي" },
  { en: "The wound is the place where the light enters you.",
    fr: "La blessure est l'endroit par où la lumière entre en vous.",
    ar: "الجرح هو الموضع الذي يدخل منه النور إليك.",
    a: "Rumi", aa: "جلال الدين الرومي" },
  { en: "What you seek is seeking you.",
    fr: "Ce que vous cherchez vous cherche aussi.",
    ar: "ما تبحث عنه يبحث عنك.",
    a: "Rumi", aa: "جلال الدين الرومي" },
  { en: "The mind is everything. What you think, you become.",
    fr: "L'esprit est tout. Vous devenez ce que vous pensez.",
    ar: "العقل هو كل شيء؛ فما تُفكّر فيه تصير إليه.",
    a: "Buddha", aa: "بوذا", d: true },

  // — Writers & thinkers —
  { en: "What you do speaks so loudly that I cannot hear what you say.",
    fr: "Ce que vous faites parle si fort que je n'entends pas ce que vous dites.",
    ar: "أفعالك تصرخ حتى إنني لا أسمع ما تقول.",
    a: "Ralph Waldo Emerson", aa: "رالف والدو إمرسون" },
  { en: "What lies behind us and what lies before us are tiny matters compared to what lies within us.",
    fr: "Ce qui est derrière nous et devant nous est peu de chose face à ce qui est en nous.",
    ar: "ما خلفنا وما أمامنا أمورٌ ضئيلة أمام ما بداخلنا.",
    a: "Ralph Waldo Emerson", aa: "رالف والدو إمرسون", d: true },
  { en: "Do not go where the path may lead; go instead where there is no path and leave a trail.",
    fr: "N'allez pas là où le chemin mène ; allez là où il n'y a pas de chemin et laissez une trace.",
    ar: "لا تسلك حيث يقودك الطريق، بل اذهب حيث لا طريق واترك أثرًا.",
    a: "Ralph Waldo Emerson", aa: "رالف والدو إمرسون", d: true },
  { en: "Go confidently in the direction of your dreams. Live the life you have imagined.",
    fr: "Avancez avec confiance dans la direction de vos rêves. Vivez la vie que vous avez imaginée.",
    ar: "امضِ بثقة نحو أحلامك، وعِش الحياة التي تخيّلتها.",
    a: "Henry David Thoreau", aa: "هنري ديفيد ثورو" },
  { en: "It is not enough to be busy. The question is: what are we busy about?",
    fr: "Être occupé ne suffit pas. La question est : occupé à quoi ?",
    ar: "لا يكفي أن تكون مشغولًا؛ السؤال: بماذا أنت مشغول؟",
    a: "Henry David Thoreau", aa: "هنري ديفيد ثورو" },
  { en: "The best way out is always through.",
    fr: "La meilleure issue est toujours de traverser.",
    ar: "أفضل مخرج هو أن تعبر من خلاله دائمًا.",
    a: "Robert Frost", aa: "روبرت فروست" },
  { en: "He who has a why to live can bear almost any how.",
    fr: "Celui qui a un pourquoi vivre peut supporter presque n'importe quel comment.",
    ar: "من له «لماذا» يعيش لأجله، يحتمل أيّ «كيف» تقريبًا.",
    a: "Friedrich Nietzsche", aa: "فريدريش نيتشه" },
  { en: "Knowing is not enough; we must apply. Willing is not enough; we must do.",
    fr: "Savoir ne suffit pas, il faut appliquer. Vouloir ne suffit pas, il faut faire.",
    ar: "لا تكفي المعرفة، بل التطبيق؛ ولا تكفي الإرادة، بل الفعل.",
    a: "Johann Wolfgang von Goethe", aa: "يوهان فولفغانغ فون غوته" },
  { en: "Courage is resistance to fear, mastery of fear — not absence of fear.",
    fr: "Le courage, c'est résister à la peur et la maîtriser, non pas l'absence de peur.",
    ar: "الشجاعة مقاومة الخوف والسيطرة عليه، لا انعدامه.",
    a: "Mark Twain", aa: "مارك توين" },
  { en: "The secret of getting ahead is getting started.",
    fr: "Le secret pour avancer, c'est de commencer.",
    ar: "سرّ التقدّم هو أن تبدأ.",
    a: "Mark Twain", aa: "مارك توين", d: true },
  { en: "Twenty years from now you will be more disappointed by the things you didn't do than by the ones you did.",
    fr: "Dans vingt ans, vous serez plus déçu par ce que vous n'aurez pas fait que par ce que vous aurez fait.",
    ar: "بعد عشرين عامًا ستندم على ما لم تفعله أكثر مما فعلته.",
    a: "H. Jackson Brown Jr.", aa: "هـ. جاكسون براون الابن" },
  { en: "A ship in harbour is safe, but that is not what ships are built for.",
    fr: "Un navire au port est en sécurité, mais ce n'est pas pour cela qu'on construit les navires.",
    ar: "السفينة في الميناء آمنة، لكن ليس لهذا تُبنى السفن.",
    a: "John A. Shedd", aa: "جون أ. شيد" },
  { en: "Energy and persistence conquer all things.",
    fr: "L'énergie et la persévérance viennent à bout de tout.",
    ar: "الهمّة والمثابرة تغلبان كل شيء.",
    a: "Benjamin Franklin", aa: "بنجامين فرانكلين" },
  { en: "An investment in knowledge pays the best interest.",
    fr: "Un investissement dans le savoir rapporte les meilleurs intérêts.",
    ar: "الاستثمار في المعرفة يعطي أعلى عائد.",
    a: "Benjamin Franklin", aa: "بنجامين فرانكلين" },
  { en: "By failing to prepare, you are preparing to fail.",
    fr: "En négligeant de vous préparer, vous vous préparez à échouer.",
    ar: "حين تُهمل الاستعداد، فأنت تستعدّ للفشل.",
    a: "Benjamin Franklin", aa: "بنجامين فرانكلين", d: true },
  { en: "Either write something worth reading or do something worth writing.",
    fr: "Écrivez quelque chose qui mérite d'être lu, ou faites quelque chose qui mérite d'être écrit.",
    ar: "إمّا أن تكتب ما يستحق أن يُقرأ، أو تفعل ما يستحق أن يُكتب.",
    a: "Benjamin Franklin", aa: "بنجامين فرانكلين" },

  // — Resilience —
  { en: "When we are no longer able to change a situation, we are challenged to change ourselves.",
    fr: "Quand nous ne pouvons plus changer une situation, nous sommes mis au défi de nous changer nous-mêmes.",
    ar: "حين نعجز عن تغيير الموقف، نُدعى إلى تغيير أنفسنا.",
    a: "Viktor Frankl", aa: "فيكتور فرانكل" },
  { en: "Between stimulus and response there is a space. In that space is our power to choose our response.",
    fr: "Entre le stimulus et la réponse, il y a un espace. Dans cet espace réside notre pouvoir de choisir.",
    ar: "بين المُثير والاستجابة مساحة، وفي تلك المساحة تكمن قدرتنا على الاختيار.",
    a: "Viktor Frankl", aa: "فيكتور فرانكل", d: true },
  { en: "It always seems impossible until it's done.",
    fr: "Cela semble toujours impossible, jusqu'à ce que ce soit fait.",
    ar: "يبدو الأمر مستحيلًا دائمًا حتى يتحقق.",
    a: "Nelson Mandela", aa: "نيلسون مانديلا" },
  { en: "Courage is not the absence of fear, but the triumph over it.",
    fr: "Le courage n'est pas l'absence de peur, mais le triomphe sur elle.",
    ar: "الشجاعة ليست غياب الخوف، بل الانتصار عليه.",
    a: "Nelson Mandela", aa: "نيلسون مانديلا" },
  { en: "The greatest glory in living lies not in never falling, but in rising every time we fall.",
    fr: "La plus grande gloire n'est pas de ne jamais tomber, mais de se relever à chaque chute.",
    ar: "ليس المجد في ألا تسقط أبدًا، بل في أن تنهض كلما سقطت.",
    a: "Nelson Mandela", aa: "نيلسون مانديلا", d: true },
  { en: "Success is not final, failure is not fatal: it is the courage to continue that counts.",
    fr: "Le succès n'est pas définitif, l'échec n'est pas fatal : c'est le courage de continuer qui compte.",
    ar: "النجاح ليس نهائيًّا والفشل ليس قاتلًا؛ الشجاعة على المواصلة هي ما يهم.",
    a: "Winston Churchill", aa: "ونستون تشرشل", d: true },
  { en: "If you're going through hell, keep going.",
    fr: "Si vous traversez l'enfer, continuez d'avancer.",
    ar: "إن كنت تمرّ بالجحيم، فواصِل المسير.",
    a: "Winston Churchill", aa: "ونستون تشرشل", d: true },
  { en: "Continuous effort — not strength or intelligence — is the key to unlocking our potential.",
    fr: "L'effort continu — ni la force ni l'intelligence — est la clé de notre potentiel.",
    ar: "الجهد المتواصل، لا القوة ولا الذكاء، هو مفتاح إمكاناتك.",
    a: "Winston Churchill", aa: "ونستون تشرشل", d: true },
  { en: "Nothing in the world can take the place of persistence.",
    fr: "Rien au monde ne remplace la persévérance.",
    ar: "لا شيء في الدنيا يحلّ محلّ المثابرة.",
    a: "Calvin Coolidge", aa: "كالفن كوليدج" },
  { en: "Do what you can, with what you have, where you are.",
    fr: "Faites ce que vous pouvez, avec ce que vous avez, là où vous êtes.",
    ar: "افعل ما تستطيع، بما لديك، حيث أنت.",
    a: "Theodore Roosevelt", aa: "ثيودور روزفلت" },
  { en: "Comparison is the thief of joy.",
    fr: "La comparaison est le voleur de joie.",
    ar: "المقارنة لصّة الفرح.",
    a: "Theodore Roosevelt", aa: "ثيودور روزفلت", d: true },
  { en: "Give me six hours to chop down a tree and I will spend the first four sharpening the axe.",
    fr: "Donnez-moi six heures pour abattre un arbre : j'en passerai quatre à aiguiser la hache.",
    ar: "امنحني ست ساعات لقطع شجرة، أُنفِق أربعًا منها في شحذ الفأس.",
    a: "Abraham Lincoln", aa: "أبراهام لينكولن", d: true },
  { en: "In the middle of difficulty lies opportunity.",
    fr: "Au milieu de la difficulté se trouve l'opportunité.",
    ar: "في قلب الصعوبة تكمن الفرصة.",
    a: "Albert Einstein", aa: "ألبرت أينشتاين", d: true },
  { en: "Try not to become a person of success, but rather a person of value.",
    fr: "Ne cherchez pas à devenir une personne de succès, mais une personne de valeur.",
    ar: "لا تسعَ لتكون إنسانًا ناجحًا، بل إنسانًا ذا قيمة.",
    a: "Albert Einstein", aa: "ألبرت أينشتاين" },
  { en: "I have not failed. I've just found ten thousand ways that won't work.",
    fr: "Je n'ai pas échoué. J'ai simplement trouvé dix mille méthodes qui ne fonctionnent pas.",
    ar: "لم أفشل، بل وجدتُ عشرة آلاف طريقة لا تنجح.",
    a: "Thomas Edison", aa: "توماس إديسون", d: true },
  { en: "Genius is one percent inspiration and ninety-nine percent perspiration.",
    fr: "Le génie, c'est un pour cent d'inspiration et quatre-vingt-dix-neuf pour cent de transpiration.",
    ar: "العبقرية واحد بالمئة إلهام وتسعة وتسعون بالمئة عرق.",
    a: "Thomas Edison", aa: "توماس إديسون" },
  { en: "Our greatest weakness lies in giving up. The most certain way to succeed is to try just one more time.",
    fr: "Notre plus grande faiblesse est d'abandonner. Le moyen le plus sûr de réussir est d'essayer une fois de plus.",
    ar: "أعظم ضعفنا هو الاستسلام، وأضمن طريق للنجاح أن تحاول مرة أخرى.",
    a: "Thomas Edison", aa: "توماس إديسون", d: true },

  // — Athletes & competitors —
  { en: "I've failed over and over and over again in my life. And that is why I succeed.",
    fr: "J'ai échoué encore et encore dans ma vie. Et c'est pour cela que je réussis.",
    ar: "فشلتُ مرارًا وتكرارًا في حياتي، ولهذا أنجح.",
    a: "Michael Jordan", aa: "مايكل جوردان" },
  { en: "Some people want it to happen, some wish it would happen, others make it happen.",
    fr: "Certains veulent que ça arrive, d'autres souhaitent que ça arrive, d'autres le font arriver.",
    ar: "بعضهم يريد أن يحدث، وبعضهم يتمنى أن يحدث، وآخرون يجعلونه يحدث.",
    a: "Michael Jordan", aa: "مايكل جوردان" },
  { en: "You must expect great things of yourself before you can do them.",
    fr: "Il faut attendre de grandes choses de soi avant de pouvoir les accomplir.",
    ar: "عليك أن تتوقع من نفسك العظائم قبل أن تقدر عليها.",
    a: "Michael Jordan", aa: "مايكل جوردان" },
  { en: "It's not the will to win that matters — everyone has that. It's the will to prepare to win that matters.",
    fr: "Ce n'est pas la volonté de gagner qui compte, tout le monde l'a. C'est la volonté de se préparer à gagner.",
    ar: "ليست إرادة الفوز ما يهم، فالجميع يملكها؛ بل إرادة الاستعداد للفوز.",
    a: "Paul “Bear” Bryant", aa: "بول «بير» براينت" },
  { en: "Champions keep playing until they get it right.",
    fr: "Les champions continuent de jouer jusqu'à ce qu'ils y arrivent.",
    ar: "الأبطال يواصلون اللعب حتى يُتقنوه.",
    a: "Billie Jean King", aa: "بيلي جين كينغ" },
  { en: "Hard work beats talent when talent doesn't work hard.",
    fr: "Le travail acharné bat le talent quand le talent ne travaille pas.",
    ar: "الاجتهاد يغلب الموهبة حين لا تجتهد الموهبة.",
    a: "Tim Notke", aa: "تيم نوتكي" },
  { en: "Everybody has a plan until they get punched in the mouth.",
    fr: "Tout le monde a un plan jusqu'à ce qu'il reçoive un coup au visage.",
    ar: "لكلٍّ خطة حتى يتلقّى لكمة على فمه.",
    a: "Mike Tyson", aa: "مايك تايسون" },
  { en: "I fear not the man who has practised ten thousand kicks once, but the man who has practised one kick ten thousand times.",
    fr: "Je ne crains pas celui qui a pratiqué dix mille coups de pied une fois, mais celui qui a pratiqué un coup de pied dix mille fois.",
    ar: "لا أخشى من تدرّب على عشرة آلاف ركلة مرة، بل من تدرّب على ركلة واحدة عشرة آلاف مرة.",
    a: "Bruce Lee", aa: "بروس لي" },
  { en: "Do not pray for an easy life; pray for the strength to endure a difficult one.",
    fr: "Ne priez pas pour une vie facile ; priez pour la force d'en endurer une difficile.",
    ar: "لا تدعُ بحياة سهلة، بل ادعُ بقوة تحتمل بها الصعبة.",
    a: "Bruce Lee", aa: "بروس لي" },
  { en: "Absorb what is useful, discard what is useless, add what is specifically your own.",
    fr: "Absorbez ce qui est utile, rejetez ce qui est inutile, ajoutez ce qui vous est propre.",
    ar: "خُذ ما ينفع، واترك ما لا ينفع، وأضِف ما هو لك وحدك.",
    a: "Bruce Lee", aa: "بروس لي" },
  { en: "The successful warrior is the average man, with laser-like focus.",
    fr: "Le guerrier qui réussit est un homme ordinaire doté d'une concentration au laser.",
    ar: "المحارب الناجح رجل عادي، لكن بتركيز كالليزر.",
    a: "Bruce Lee", aa: "بروس لي" },

  // — Modern practitioners —
  { en: "You do not rise to the level of your goals. You fall to the level of your systems.",
    fr: "Vous ne vous élevez pas au niveau de vos objectifs : vous retombez au niveau de vos systèmes.",
    ar: "أنت لا ترتقي إلى مستوى أهدافك، بل تهبط إلى مستوى أنظمتك.",
    a: "James Clear", aa: "جيمس كلير" },
  { en: "Every action you take is a vote for the type of person you wish to become.",
    fr: "Chaque action que vous faites est un vote pour la personne que vous souhaitez devenir.",
    ar: "كل فعل تقوم به صوتٌ تمنحه للشخص الذي تريد أن تصيره.",
    a: "James Clear", aa: "جيمس كلير" },
  { en: "Habits are the compound interest of self-improvement.",
    fr: "Les habitudes sont les intérêts composés du développement personnel.",
    ar: "العادات هي الفائدة المركّبة لتطوير الذات.",
    a: "James Clear", aa: "جيمس كلير" },
  { en: "Grit is passion and perseverance for very long-term goals.",
    fr: "La ténacité, c'est la passion et la persévérance au service d'objectifs à très long terme.",
    ar: "العزيمة شغفٌ ومثابرة نحو أهداف بعيدة المدى.",
    a: "Angela Duckworth", aa: "أنجيلا داكورث" },
  { en: "Enthusiasm is common. Endurance is rare.",
    fr: "L'enthousiasme est courant. L'endurance est rare.",
    ar: "الحماس شائع، أما الصبر فنادر.",
    a: "Angela Duckworth", aa: "أنجيلا داكورث" },
  { en: "Becoming is better than being.",
    fr: "Devenir vaut mieux qu'être.",
    ar: "أن تصير خيرٌ من أن تكون.",
    a: "Carol Dweck", aa: "كارول دويك" },
  { en: "Discipline equals freedom.",
    fr: "La discipline, c'est la liberté.",
    ar: "الانضباط يساوي الحرية.",
    a: "Jocko Willink", aa: "جوكو ويلينك" },
  { en: "The obstacle is the way.",
    fr: "L'obstacle est le chemin.",
    ar: "العقبة هي الطريق.",
    a: "Ryan Holiday", aa: "رايان هوليداي" },
  { en: "The most important conversations you'll ever have are the ones you'll have with yourself.",
    fr: "Les conversations les plus importantes de votre vie sont celles que vous aurez avec vous-même.",
    ar: "أهم الحوارات في حياتك هي تلك التي تخوضها مع نفسك.",
    a: "David Goggins", aa: "ديفيد غوغينز" },
  { en: "Motivation is crap. Motivation comes and goes.",
    fr: "La motivation ne vaut rien. Elle va et vient.",
    ar: "الحماس لا قيمة له؛ فهو يأتي ويذهب.",
    a: "David Goggins", aa: "ديفيد غوغينز" },
  { en: "You are in danger of living a life so comfortable that you will die without realising your true potential.",
    fr: "Vous risquez de mener une vie si confortable que vous mourrez sans avoir réalisé votre véritable potentiel.",
    ar: "أنت مهدَّد بحياة مريحة إلى حدّ أن تموت دون أن تدرك حقيقة إمكاناتك.",
    a: "David Goggins", aa: "ديفيد غوغينز" },
  { en: "It is in your moments of decision that your destiny is shaped.",
    fr: "C'est dans vos moments de décision que se façonne votre destin.",
    ar: "في لحظات قرارك يتشكّل مصيرك.",
    a: "Tony Robbins", aa: "توني روبنز" },
  { en: "Setting goals is the first step in turning the invisible into the visible.",
    fr: "Se fixer des objectifs est le premier pas pour rendre visible l'invisible.",
    ar: "وضع الأهداف أول خطوة لتحويل غير المرئي إلى مرئي.",
    a: "Tony Robbins", aa: "توني روبنز" },
  { en: "Where focus goes, energy flows.",
    fr: "Là où va l'attention, l'énergie suit.",
    ar: "حيث يذهب تركيزك تتدفّق طاقتك.",
    a: "Tony Robbins", aa: "توني روبنز" },
  { en: "Successful people are simply those with successful habits.",
    fr: "Les gens qui réussissent sont simplement ceux qui ont des habitudes qui réussissent.",
    ar: "الناجحون هم ببساطة أصحاب العادات الناجحة.",
    a: "Brian Tracy", aa: "برايان تريسي" },
  { en: "Whatever we expect with confidence becomes our own self-fulfilling prophecy.",
    fr: "Tout ce que nous attendons avec confiance devient notre propre prophétie autoréalisatrice.",
    ar: "كل ما نتوقعه بثقة يصير نبوءةً تُحقّق نفسها.",
    a: "Brian Tracy", aa: "برايان تريسي" },
  { en: "What we fear doing most is usually what we most need to do.",
    fr: "Ce que nous redoutons le plus de faire est souvent ce que nous devons faire le plus.",
    ar: "ما نخشى فعله أكثر هو غالبًا ما نحتاج إلى فعله أكثر.",
    a: "Tim Ferriss", aa: "تيم فيريس" },
  { en: "A person's success in life can usually be measured by the number of uncomfortable conversations they are willing to have.",
    fr: "La réussite d'une personne se mesure souvent au nombre de conversations inconfortables qu'elle accepte d'avoir.",
    ar: "يُقاس نجاح المرء غالبًا بعدد المحادثات غير المريحة التي يقبل خوضها.",
    a: "Tim Ferriss", aa: "تيم فيريس" },
  { en: "Focus is a matter of deciding what you're not going to do.",
    fr: "La concentration consiste à décider ce que vous n'allez pas faire.",
    ar: "التركيز أن تُقرّر ما لن تفعله.",
    a: "John Carmack", aa: "جون كارماك" },
  { en: "What gets measured gets managed.",
    fr: "Ce qui se mesure se gère.",
    ar: "ما يُقاس يُدار.",
    a: "Peter Drucker", aa: "بيتر دراكر", d: true },
  { en: "Efficiency is doing things right; effectiveness is doing the right things.",
    fr: "L'efficience, c'est bien faire les choses ; l'efficacité, c'est faire les bonnes choses.",
    ar: "الكفاءة أن تفعل الأشياء بشكل صحيح، والفعالية أن تفعل الأشياء الصحيحة.",
    a: "Peter Drucker", aa: "بيتر دراكر" },
  { en: "The best way to predict the future is to create it.",
    fr: "La meilleure façon de prédire l'avenir est de le créer.",
    ar: "أفضل وسيلة للتنبؤ بالمستقبل أن تصنعه.",
    a: "Peter Drucker", aa: "بيتر دراكر", d: true },
  { en: "Your time is limited, so don't waste it living someone else's life.",
    fr: "Votre temps est limité : ne le gaspillez pas à vivre la vie de quelqu'un d'autre.",
    ar: "وقتك محدود، فلا تُهدره عائشًا حياة شخص آخر.",
    a: "Steve Jobs", aa: "ستيف جوبز" },
];

/** How "attributed to" is phrased per language. */
const ATTRIB = { en: "attributed to", fr: "attribué à", ar: "منسوب إلى" };

/** Days elapsed since the Unix epoch, in UTC — the same value worldwide for a
 *  given calendar day, so everyone sees the same quote. */
export function dayIndex(date = new Date()) {
  return Math.floor(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()) / 86400000);
}

// The list is grouped by theme, so stepping one entry per day would show three
// Michael Jordan quotes on three consecutive days. STRIDE is coprime with 100,
// so multiplying by it still visits all 100 exactly once per cycle while
// scattering neighbours far apart.
const STRIDE = 37;

/** Today's quote in the requested language. Deterministic: no randomness, no
 *  storage, no user control. */
export function quoteOfTheDay(lang = "en", date = new Date()) {
  const L = ATTRIB[lang] ? lang : "en";
  const n = QUOTES.length;
  const q = QUOTES[(((dayIndex(date) * STRIDE) % n) + n) % n];
  const name = L === "ar" ? q.aa : q.a;
  return { text: q[L] || q.en, author: q.d ? `${ATTRIB[L]} ${name}` : name };
}
